// ===========================================
// ANIMATION CONFIG STORE
// ===========================================
// Stores animation timing values received from server on table join.
// These are the AUTHORITATIVE values - client caches them for the session
// and uses them as fallbacks when individual events are missing duration.
//
// IMPORTANT: Using a fallback is an ERROR condition. It means the server
// failed to include duration in an event. This should be logged and fixed.

import { create } from 'zustand';
import { useAdminLogStore } from './adminLogStore';

// ===========================================
// TYPES
// ===========================================

// Animation config keys - must match server's RuntimeConfig['animations']
export type AnimationConfigKey =
  // Server-paced animations (server waits after emitting)
  | 'cardDeal'
  | 'cardDealDealer'
  | 'cardDealInterval'
  | 'cardFlip'
  | 'cardHit'
  | 'chipFlight'
  | 'chipsPaid'
  | 'chipsCollected'
  | 'cardsCleared'
  | 'splitHands'
  | 'sidebetPlaced'
  | 'sidebetChipsPaid'
  | 'sidebetChipsCollected'
  // Betting phase
  | 'betStaged'
  | 'betConfirmed'
  | 'betUnstaged'
  // Dealing phase
  | 'handBoxAppear'
  | 'scoreUpdate'
  // Player turn
  | 'turnIndicatorShow'
  | 'actionButtonsShow'
  | 'actionTaken'
  | 'doubleDown'
  // Special outcomes
  | 'bust'
  | 'blackjack'
  | 'surrender'
  // Insurance
  | 'insurancePrompt'
  | 'insuranceDecision'
  | 'insuranceResolve'
  | 'insurancePlacement'
  // Dealer turn
  | 'holeCardReveal'
  | 'dealerHit'
  | 'dealerHitInterval'
  | 'dealerStand'
  | 'dealerBust'
  | 'dealerBlackjack'
  // Resolution
  | 'handOutcomeShow'
  | 'chipStackUpdate'
  // Round transitions
  | 'roundComplete'
  | 'roundStart'
  | 'bettingOpened'
  // Global
  | 'globalSpeedMultiplier'
  // Client-only animations (UI feedback, server doesn't pace)
  | 'railToStaged'
  | 'stagedToRail'
  | 'stagedToKitty'
  | 'chipSwapDelay'
  | 'chipSwapOut'
  | 'chipSwapIn'
  | 'chipSwapInDelay'
  | 'railToInsuranceKitty'
  | 'insuranceKittyShift'
  | 'clearChipStagger'
  // PixiJS card/chip micro-animations
  | 'buildYourBetPulse'
  | 'cardFlipInFlight'
  | 'cardNudge'
  | 'chipLandingScale'
  | 'chipFadeOut'
  | 'cardStraighten'
  | 'outcomeLabelFly';

// Stagger config keys - must match server's StaggerConfig
export type StaggerConfigKey =
  | 'chipPlacement'
  | 'chipPayout'
  | 'chipCollection'
  | 'chipToDealer'
  | 'chipMidRoundBust'
  | 'cardRake'
  | 'sidebetChip'
  | 'insuranceChip'
  | 'doubleDownChip'
  | 'betConfirmChip';

export interface AnimationConfig {
  // Server-paced animations
  cardDeal: number;
  cardDealDealer: number;
  cardDealInterval: number;
  cardFlip: number;
  cardHit: number;
  chipFlight: number;
  chipsPaid: number;
  chipsCollected: number;
  cardsCleared: number;
  splitHands: number;
  sidebetPlaced: number;
  sidebetChipsPaid: number;
  sidebetChipsCollected: number;
  // Betting phase
  betStaged: number;
  betConfirmed: number;
  betUnstaged: number;
  // Dealing phase
  handBoxAppear: number;
  scoreUpdate: number;
  // Player turn
  turnIndicatorShow: number;
  actionButtonsShow: number;
  actionTaken: number;
  doubleDown: number;
  // Special outcomes
  bust: number;
  blackjack: number;
  surrender: number;
  // Insurance
  insurancePrompt: number;
  insuranceDecision: number;
  insuranceResolve: number;
  insurancePlacement: number;
  // Dealer turn
  holeCardReveal: number;
  dealerHit: number;
  dealerHitInterval: number;
  dealerStand: number;
  dealerBust: number;
  dealerBlackjack: number;
  // Resolution
  handOutcomeShow: number;
  chipStackUpdate: number;
  // Round transitions
  roundComplete: number;
  roundStart: number;
  bettingOpened: number;
  // Global
  globalSpeedMultiplier: number;
  // Client-only animations
  railToStaged: number;
  stagedToRail: number;
  stagedToKitty: number;
  chipSwapDelay: number;
  chipSwapOut: number;
  chipSwapIn: number;
  chipSwapInDelay: number;
  railToInsuranceKitty: number;
  insuranceKittyShift: number;
  clearChipStagger: number;
  // PixiJS card/chip micro-animations
  buildYourBetPulse: number;
  cardFlipInFlight: number;
  cardNudge: number;
  chipLandingScale: number;
  chipFadeOut: number;
  cardStraighten: number;
  outcomeLabelFly: number;
}

// UI delay config keys
export type UIDelayConfigKey =
  | 'insuranceChipWait'
  | 'doubleDownChipWait'
  | 'chipConsolidationSwap';

export interface UIDelayConfig {
  insuranceChipWait: number;
  doubleDownChipWait: number;
  chipConsolidationSwap: number;
}

// Timer config keys
export type TimerConfigKey =
  | 'bettingInvisiblePhase'
  | 'bettingVisiblePhase'
  | 'maxConsecutiveSitOuts'
  | 'insuranceInvisiblePhase'
  | 'insuranceVisiblePhase'
  | 'turnInvisiblePhase'
  | 'turnVisiblePhase';

export interface TimerConfig {
  bettingInvisiblePhase: number;
  bettingVisiblePhase: number;
  maxConsecutiveSitOuts: number;
  insuranceInvisiblePhase: number;
  insuranceVisiblePhase: number;
  turnInvisiblePhase: number;
  turnVisiblePhase: number;
}

// Pause config keys
export type PauseConfigKey =
  | 'afterDeal'
  | 'afterBlackjackCheck'
  | 'afterBust'
  | 'afterDealerReveal'
  | 'afterResolution'
  | 'betweenRounds'
  | 'clientEventProcessing';

export interface PauseConfig {
  afterDeal: number;
  afterBlackjackCheck: number;
  afterBust: number;
  afterDealerReveal: number;
  afterResolution: number;
  betweenRounds: number;
  clientEventProcessing: number;
}

export interface StaggerConfig {
  chipPlacement: number;
  chipPayout: number;
  chipCollection: number;
  chipToDealer: number;
  chipMidRoundBust: number;
  cardRake: number;
  sidebetChip: number;
  insuranceChip: number;
  doubleDownChip: number;
  betConfirmChip: number;
}

interface AnimationConfigState {
  // The cached config from server (null until loaded)
  config: AnimationConfig | null;

  // The cached stagger config from server (null until loaded)
  stagger: StaggerConfig | null;

  // The cached UI delay config from server (null until loaded)
  uiDelays: UIDelayConfig | null;

  // The cached timer config from server (null until loaded)
  timers: TimerConfig | null;

  // The cached pause config from server (null until loaded)
  pauses: PauseConfig | null;

  // Whether config has been loaded this session
  isLoaded: boolean;

  // Set config received from server (all configs)
  setConfig: (
    config: AnimationConfig,
    stagger: StaggerConfig,
    uiDelays: UIDelayConfig,
    timers: TimerConfig,
    pauses: PauseConfig
  ) => void;

  // Clear config (call on session end / table leave)
  clearConfig: () => void;

  // Get a duration value - returns serverDuration if provided, else cached config value
  // Logs HIGH severity error if fallback is used
  getValue: (
    key: AnimationConfigKey,
    serverDuration: number | undefined,
    context: { fileName: string; functionName: string; eventType?: string }
  ) => number;

  // Get a stagger value - returns serverStagger if provided, else cached stagger value
  // Logs HIGH severity error if fallback is used
  getStaggerValue: (
    key: StaggerConfigKey,
    serverStagger: number | undefined,
    context: { fileName: string; functionName: string; eventType?: string }
  ) => number;

  // Get raw cached value (no fallback logging)
  getCachedValue: (key: AnimationConfigKey) => number | null;

  // Get raw cached stagger value (no fallback logging)
  getCachedStagger: (key: StaggerConfigKey) => number | null;

  // Get raw cached UI delay value (no fallback logging)
  getCachedUIDelay: (key: UIDelayConfigKey) => number | null;

  // Get raw cached timer value (no fallback logging)
  getCachedTimer: (key: TimerConfigKey) => number | null;

  // Get raw cached pause value (no fallback logging)
  getCachedPause: (key: PauseConfigKey) => number | null;
}

// ===========================================
// DIAGNOSTIC LOGGING
// ===========================================

function logFallbackUsage(
  key: AnimationConfigKey,
  fallbackValue: number,
  context: { fileName: string; functionName: string; eventType?: string }
): void {
  // Log to console for diagnostic logging
  console.error(
    `[ANIMATION CONFIG] FALLBACK USED: ${key} - server event missing duration, using cached value ${fallbackValue}ms`,
    { context }
  );

  // Log to admin log store with HIGH severity (error level)
  useAdminLogStore.getState().recordLog({
    category: 'error',
    severity: 'error', // HIGH severity
    fileName: context.fileName,
    functionName: context.functionName,
    description: `Animation fallback used: ${key}`,
    details: {
      errorMessage: `Server event missing duration for ${key}. Using cached fallback: ${fallbackValue}ms`,
      context: {
        animationKey: key,
        fallbackValue,
        eventType: context.eventType,
      },
    },
  });
}

function logConfigNotLoaded(
  key: AnimationConfigKey | StaggerConfigKey,
  configType: 'animation' | 'stagger',
  context: { fileName: string; functionName: string; eventType?: string }
): void {
  // This is a CRITICAL error - config should always be loaded before animations
  console.error(
    `[ANIMATION CONFIG] CRITICAL: Config not loaded when requesting ${configType} ${key}`,
    { context }
  );

  useAdminLogStore.getState().recordLog({
    category: 'error',
    severity: 'critical',
    fileName: context.fileName,
    functionName: context.functionName,
    description: `Animation config not loaded: ${key}`,
    details: {
      errorMessage: `Attempted to get ${configType} value for ${key} but config was never loaded from server. This should not happen - table join should be blocked until config is received.`,
      context: {
        configType,
        configKey: key,
        eventType: context.eventType,
      },
    },
  });
}

function logStaggerFallbackUsage(
  key: StaggerConfigKey,
  fallbackValue: number,
  context: { fileName: string; functionName: string; eventType?: string }
): void {
  // Log to console for diagnostic logging
  console.error(
    `[ANIMATION CONFIG] STAGGER FALLBACK USED: ${key} - server event missing stagger, using cached value ${fallbackValue}ms`,
    { context }
  );

  // Log to admin log store with HIGH severity (error level)
  useAdminLogStore.getState().recordLog({
    category: 'error',
    severity: 'error', // HIGH severity
    fileName: context.fileName,
    functionName: context.functionName,
    description: `Stagger fallback used: ${key}`,
    details: {
      errorMessage: `Server event missing stagger for ${key}. Using cached fallback: ${fallbackValue}ms`,
      context: {
        staggerKey: key,
        fallbackValue,
        eventType: context.eventType,
      },
    },
  });
}

// ===========================================
// EMERGENCY FALLBACK VALUES
// ===========================================
// These are ONLY used if config was never loaded (which should never happen).
// They exist solely to prevent crashes, not as a normal code path.

const EMERGENCY_FALLBACKS: AnimationConfig = {
  // Server-paced animations
  cardDeal: 400,
  cardDealDealer: 315,
  cardDealInterval: 0,
  cardFlip: 250,
  cardHit: 400,
  chipFlight: 200,
  chipsPaid: 800,
  chipsCollected: 800,
  cardsCleared: 450,
  splitHands: 600,
  sidebetPlaced: 200,
  sidebetChipsPaid: 400,
  sidebetChipsCollected: 200,
  // Betting phase
  betStaged: 200,
  betConfirmed: 200,
  betUnstaged: 150,
  // Dealing phase
  handBoxAppear: 300,
  scoreUpdate: 100,
  // Player turn
  turnIndicatorShow: 200,
  actionButtonsShow: 150,
  actionTaken: 100,
  doubleDown: 500,
  // Special outcomes
  bust: 500,
  blackjack: 800,
  surrender: 500,
  // Insurance
  insurancePrompt: 300,
  insuranceDecision: 200,
  insuranceResolve: 1000,
  insurancePlacement: 250,
  // Dealer turn
  holeCardReveal: 500,
  dealerHit: 400,
  dealerHitInterval: 200,
  dealerStand: 200,
  dealerBust: 600,
  dealerBlackjack: 600,
  // Resolution
  handOutcomeShow: 100,
  chipStackUpdate: 200,
  // Round transitions
  roundComplete: 500,
  roundStart: 300,
  bettingOpened: 400,
  // Global
  globalSpeedMultiplier: 1.0,
  // Client-only animations
  railToStaged: 200,
  stagedToRail: 150,
  stagedToKitty: 250,
  chipSwapDelay: 200,
  chipSwapOut: 150,
  chipSwapIn: 200,
  chipSwapInDelay: 100,
  railToInsuranceKitty: 250,
  insuranceKittyShift: 200,
  clearChipStagger: 80,
  // PixiJS card/chip micro-animations
  buildYourBetPulse: 2000,
  cardFlipInFlight: 120,
  cardNudge: 150,
  chipLandingScale: 150,
  chipFadeOut: 150,
  cardStraighten: 150,
  outcomeLabelFly: 300,
};

const EMERGENCY_STAGGER_FALLBACKS: StaggerConfig = {
  chipPlacement: 30,
  chipPayout: 80,
  chipCollection: 80,
  chipToDealer: 80,
  chipMidRoundBust: 60,
  cardRake: 150,
  sidebetChip: 50,
  insuranceChip: 50,
  doubleDownChip: 80,
  betConfirmChip: 80,
};

const EMERGENCY_UIDELAY_FALLBACKS: UIDelayConfig = {
  insuranceChipWait: 300,
  doubleDownChipWait: 400,
  chipConsolidationSwap: 200,
};

const EMERGENCY_TIMER_FALLBACKS: TimerConfig = {
  bettingInvisiblePhase: 20000,
  bettingVisiblePhase: 10000,
  maxConsecutiveSitOuts: 3,
  insuranceInvisiblePhase: 20000,
  insuranceVisiblePhase: 10000,
  turnInvisiblePhase: 20000,
  turnVisiblePhase: 10000,
};

const EMERGENCY_PAUSE_FALLBACKS: PauseConfig = {
  afterDeal: 400,
  afterBlackjackCheck: 200,
  afterBust: 300,
  afterDealerReveal: 400,
  afterResolution: 300,
  betweenRounds: 600,
  clientEventProcessing: 100,
};

// ===========================================
// STORE IMPLEMENTATION
// ===========================================

export const useAnimationConfigStore = create<AnimationConfigState>((set, get) => ({
  config: null,
  stagger: null,
  uiDelays: null,
  timers: null,
  pauses: null,
  isLoaded: false,

  setConfig: (config, stagger, uiDelays, timers, pauses) => {
    console.log('[AnimationConfig] Config loaded from server:', { config, stagger, uiDelays, timers, pauses });
    set({ config, stagger, uiDelays, timers, pauses, isLoaded: true });
  },

  clearConfig: () => {
    console.log('[AnimationConfig] Config cleared (session ended)');
    set({ config: null, stagger: null, uiDelays: null, timers: null, pauses: null, isLoaded: false });
  },

  getValue: (key, serverDuration, context) => {
    // If server provided duration, use it directly (no fallback needed)
    if (serverDuration !== undefined && serverDuration > 0) {
      return serverDuration;
    }

    // Server didn't provide duration - need to use fallback
    const { config, isLoaded } = get();

    if (!isLoaded || !config) {
      // Config never loaded - this is a critical error
      logConfigNotLoaded(key, 'animation', context);
      return EMERGENCY_FALLBACKS[key];
    }

    // Config is loaded - use cached value but log the fallback usage
    const fallbackValue = config[key];
    logFallbackUsage(key, fallbackValue, context);
    return fallbackValue;
  },

  getStaggerValue: (key, serverStagger, context) => {
    // If server provided stagger, use it directly (no fallback needed)
    if (serverStagger !== undefined && serverStagger > 0) {
      return serverStagger;
    }

    // Server didn't provide stagger - need to use fallback
    const { stagger, isLoaded } = get();

    if (!isLoaded || !stagger) {
      // Config never loaded - this is a critical error
      logConfigNotLoaded(key, 'stagger', context);
      return EMERGENCY_STAGGER_FALLBACKS[key];
    }

    // Config is loaded - use cached value but log the fallback usage
    const fallbackValue = stagger[key];
    logStaggerFallbackUsage(key, fallbackValue, context);
    return fallbackValue;
  },

  getCachedValue: (key) => {
    const { config } = get();
    return config ? config[key] : null;
  },

  getCachedStagger: (key) => {
    const { stagger } = get();
    return stagger ? stagger[key] : null;
  },

  getCachedUIDelay: (key) => {
    const { uiDelays, isLoaded } = get();
    if (!isLoaded || !uiDelays) {
      return EMERGENCY_UIDELAY_FALLBACKS[key];
    }
    return uiDelays[key];
  },

  getCachedTimer: (key) => {
    const { timers, isLoaded } = get();
    if (!isLoaded || !timers) {
      return EMERGENCY_TIMER_FALLBACKS[key];
    }
    return timers[key];
  },

  getCachedPause: (key) => {
    const { pauses, isLoaded } = get();
    if (!isLoaded || !pauses) {
      return EMERGENCY_PAUSE_FALLBACKS[key];
    }
    return pauses[key];
  },
}));

// ===========================================
// HELPER FUNCTION FOR COMMON USE CASE
// ===========================================

/**
 * Get animation duration - use server-provided value or fallback to cached config.
 * This is a convenience wrapper that logs errors when fallbacks are used.
 *
 * @param key - The animation config key
 * @param serverDuration - Duration from server event (undefined if not provided)
 * @param context - Location info for error logging
 * @returns The duration in milliseconds
 */
export function getAnimationDuration(
  key: AnimationConfigKey,
  serverDuration: number | undefined,
  context: { fileName: string; functionName: string; eventType?: string }
): number {
  return useAnimationConfigStore.getState().getValue(key, serverDuration, context);
}

/**
 * Get stagger value - use server-provided value or fallback to cached config.
 * This is a convenience wrapper that logs errors when fallbacks are used.
 *
 * @param key - The stagger config key
 * @param serverStagger - Stagger from server event (undefined if not provided)
 * @param context - Location info for error logging
 * @returns The stagger delay in milliseconds
 */
export function getStaggerValue(
  key: StaggerConfigKey,
  serverStagger: number | undefined,
  context: { fileName: string; functionName: string; eventType?: string }
): number {
  return useAnimationConfigStore.getState().getStaggerValue(key, serverStagger, context);
}
